import React from 'react';

import {
  BearingEnvelope,
  CeilingPlane,
  CeilingTray,
  FeaturePatternTrussContainer,
  LayoutFeatureArea,
  LayoutModel,
  RoofPlane,
  TrussEnvelope,
} from '@paragon/contract-library';
import {
  BuildingSchema,
  editLayoutModelProperties,
  getSchemaForProjectHierarchyModel,
  LevelSchema,
  ModelProperty,
  ProjectSchema,
  PropertiesPanel as LayoutModelsPropertiesPanel,
  PropertyList,
  saveAllProjectChanges,
} from '@paragon/layout-models';
import { humanCase } from '@paragon/utilities';

import { SelectableItemEntry } from '../../../library/selection/types';
import { getSelectedItemEntries } from '../../../store/selectors/getSelectedItemSelector';
import { propertiesShouldBeViewOnly } from '../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { AnalogPanelProperties } from '../subpanels/properties/AnalogPanelProperties';
import { BearingProperties } from '../subpanels/properties/BearingProperties';
import { BulkBearingEnvelopeProperties } from '../subpanels/properties/BulkBearingEnvelopeProperties';
import { BulkFeaturePatternContainerProperties } from '../subpanels/properties/BulkFeaturePatternContainerProperties';
import { BulkPlaneProperties } from '../subpanels/properties/BulkPlaneProperties';
import { BulkTrussEnvelopeProperties } from '../subpanels/properties/BulkTrussEnvelopeProperties';
import { CarriedComponentConnectionEdit } from '../subpanels/properties/CarriedComponentConnectionEdit';
import { ComponentDesignProperties } from '../subpanels/properties/ComponentDesignProperties';
import { ActiveComponentEndConditionPropertiesPanel } from '../subpanels/properties/EndConditionSettings';
import { FeaturePatternContainerProperties } from '../subpanels/properties/FeaturePatternContainerProperties';
import { GussetProperties } from '../subpanels/properties/GussetPropertySubPanel';
import { HangerProperties } from '../subpanels/properties/HangerProperties';
import { JointProperties } from '../subpanels/properties/JointProperties';
import {
  BulkFeatureAreaPropertiesPanel,
  FeatureAreaPropertiesPanel,
} from '../subpanels/properties/layout-feature-areas/FeatureAreaPropertiesPanel';
import { LinkProperties } from '../subpanels/properties/LinkProperties';
import { ManualLoadSettingsEdit } from '../subpanels/properties/ManualLoadSettingsEdit';
import { MemberProperties } from '../subpanels/properties/members/MemberProperties';
import { MovingLoadSettingsEdit } from '../subpanels/properties/MovingLoadSettingsEdit';
import { PlatePairProperties } from '../subpanels/properties/PlatePairProperties';
import { ReferenceLineProperties } from '../subpanels/properties/ReferenceLineProperties';
import { BearingEnvelopeProperties } from '../subpanels/properties/roof-planes/bearing-envelopes/BearingEnvelopeProperties';
import { CeilingPlaneProperties } from '../subpanels/properties/roof-planes/CeilingPlaneProperties';
import {
  BulkCeilingTrayProperties,
  CeilingTrayProperties,
} from '../subpanels/properties/roof-planes/CeilingTrayProperties';
import { RoofPlaneProperties } from '../subpanels/properties/roof-planes/RoofPlaneProperties';
import { RoofContainerProperties } from '../subpanels/properties/RoofContainerProperties';
import { SnappingUnderlayProperties } from '../subpanels/properties/SnappingUnderlayProperties';
import { SpliceProperties } from '../subpanels/properties/SpliceProperties';
import { TrussEnvelopeProperties } from '../subpanels/properties/TrussEnvelopeProperties';
import { WorkLineProperties } from '../subpanels/properties/WorkLineProperties';
import { TrussGroupPropertiesPanel } from '../subpanels/TrussGroupPropertiesPanel';

export function PropertiesPanel() {
  const selectedEntries = useAppSelector(getSelectedItemEntries);
  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);

  if (selectedEntries.length === 0) return <div />;

  const { type } = selectedEntries[0];

  // mixed-type property panels are not supported
  if (selectedEntries.some((entry) => entry.type !== type)) return <div />;

  // dual-mode panels that support either single or multiple selected entries
  switch (type) {
    case 'workLine':
      return <WorkLineProperties entries={selectedEntries} />;

    default:
    // intentional fallthrough
  }

  switch (selectedEntries.length) {
    // panels that support only one selected entry
    case 1: {
      const selectedEntry = selectedEntries[0];

      if (needsLayoutModelPropertyPanel(selectedEntries)) {
        return <LayoutModelPropertiesPanel isViewOnly={isViewOnly} selectedEntries={selectedEntries} />;
      }

      switch (selectedEntry.type) {
        case 'componentDesign':
        case 'productionComponentInstance':
          return <ComponentDesignProperties />;

        case 'analogLink':
          return <LinkProperties />;

        case 'analogJoint':
          return <JointProperties />;

        case 'platePair':
          return <PlatePairProperties />;

        case 'bearing':
          return <BearingProperties />;

        case 'member':
        case 'brace':
          return <MemberProperties />;

        case 'gusset':
          return <GussetProperties />;

        case 'hanger':
          return <HangerProperties value={selectedEntry.item} />;

        case 'panel':
          return <AnalogPanelProperties analogPanel={selectedEntry.item} />;

        case 'splice':
          return <SpliceProperties />;

        case 'endCondition':
          return <ActiveComponentEndConditionPropertiesPanel guid={selectedEntry.item.guid} />;

        case 'referenceLine':
          return <ReferenceLineProperties allowMemberLines line={selectedEntry.item} />;

        case 'manualLoad':
          return <ManualLoadSettingsEdit manualLoadSettings={selectedEntry.item} />;

        case 'movingLoad':
          return <MovingLoadSettingsEdit movingLoadSettings={selectedEntry.item} />;

        case 'jackGirderConnection':
          return <CarriedComponentConnectionEdit carriedComponentConnection={selectedEntry.item} />;

        case 'trussEnvelope':
          return <TrussEnvelopeProperties value={selectedEntry.item} />;

        case 'trussGroup':
          return <TrussGroupPropertiesPanel value={selectedEntry.item} />;

        case 'roofPlane':
          return <RoofPlaneProperties value={selectedEntry.item} />;

        case 'ceilingPlane':
          return <CeilingPlaneProperties value={selectedEntry.item} />;

        case 'ceilingTray':
          return <CeilingTrayProperties value={selectedEntry.item} />;

        case 'roofContainer':
          return <RoofContainerProperties value={selectedEntry.item} />;

        case 'beam':
        case 'wall':
          return <BearingEnvelopeProperties value={selectedEntry.item} />;

        case 'featurePatternContainer':
          return <FeaturePatternContainerProperties value={selectedEntry.item} />;

        case 'featureArea':
          return <FeatureAreaPropertiesPanel value={selectedEntry.item} />;

        case 'snappingUnderlay':
          return <SnappingUnderlayProperties value={selectedEntry.item} />;

        case 'profileJoint':
        case 'webJointSegment':
        default:
          return <div />;
      }
    }

    // panels that support multiple selected entries
    default: {
      switch (type) {
        case 'beam':
        case 'wall':
          // TODO: typescript
          return (
            <BulkBearingEnvelopeProperties
              type={type}
              envelopes={selectedEntries.map((entry) => entry.item) as BearingEnvelope[]}
            />
          );

        case 'ceilingTray':
          return <BulkCeilingTrayProperties trays={selectedEntries.map((entry) => entry.item) as CeilingTray[]} />;

        case 'roofPlane':
          // TODO: typescript
          return <BulkPlaneProperties type="roof" planes={selectedEntries.map((entry) => entry.item) as RoofPlane[]} />;

        case 'ceilingPlane':
          // TODO: typescript
          return (
            <BulkPlaneProperties type="ceiling" planes={selectedEntries.map((entry) => entry.item) as CeilingPlane[]} />
          );

        case 'trussEnvelope':
          // TODO: typescript
          return (
            <BulkTrussEnvelopeProperties envelopes={selectedEntries.map((entry) => entry.item) as TrussEnvelope[]} />
          );

        case 'member':
          return <MemberProperties />;

        case 'platePair':
          return <PlatePairProperties />;

        case 'featureArea':
          return (
            <BulkFeatureAreaPropertiesPanel
              values={selectedEntries.map((entry) => entry.item) as LayoutFeatureArea[]}
            />
          );

        case 'featurePatternContainer':
          return (
            <BulkFeaturePatternContainerProperties
              containers={selectedEntries.map((entry) => entry.item) as FeaturePatternTrussContainer[]}
            />
          );

        default:
          return <div />;
      }
    }
  }
}

function needsLayoutModelPropertyPanel(selectedEntries: SelectableItemEntry[]): boolean {
  return [ProjectSchema.type, BuildingSchema.type, LevelSchema.type].some((layoutModelType) =>
    selectedEntries.some((entry) => entry.type === layoutModelType),
  );
}

export function LayoutModelPropertiesPanel(props: {
  readonly isViewOnly: boolean;
  readonly selectedEntries: readonly SelectableItemEntry[];
  readonly updateLayoutModel?: (guid: string, editedProperties: Record<string, any>) => void;
}) {
  const dispatch = useAppDispatch();

  function editLayoutModel(guid: string, editedProperties: Record<string, any>) {
    if (props.isViewOnly) return;

    if (props.updateLayoutModel) {
      props.updateLayoutModel(guid, editedProperties);
    } else {
      dispatch(editLayoutModelProperties(guid, editedProperties));
      dispatch(saveAllProjectChanges());
    }
  }

  // Multi-select? We don't do that here.
  if (props.selectedEntries.length !== 1) return <div />;

  const selectedLayoutModelEntry = props.selectedEntries[0];

  const modelSchema = getSchemaForProjectHierarchyModel(selectedLayoutModelEntry.item as LayoutModel);
  const modelProperties = Object.keys(modelSchema.properties).map((propertyName) => (
    <ModelProperty
      property={propertyName as keyof LayoutModel}
      model={selectedLayoutModelEntry.item as LayoutModel}
      disabled={props.isViewOnly}
      onEdit={editLayoutModel}
      modelSchema={modelSchema}
      key={propertyName}
    />
  ));

  return (
    <LayoutModelsPropertiesPanel
      key={`${selectedLayoutModelEntry.type}Properties`}
      title={`${humanCase(selectedLayoutModelEntry.type)} Properties`}
    >
      <PropertyList sx={{ marginTop: 2 }}>{modelProperties}</PropertyList>
    </LayoutModelsPropertiesPanel>
  );
}
